





















































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import { EmployerCardIssueCriteriaResponse, Employer, CreateCardIssueCriteriaRequest } from "@/admin/types";
import { useEmployerFeeScheduleStore } from "@/admin/store/employerFeeSchedule";
import { useEmployerInterestRateStore } from "@/admin/store/employerInterestRate";
import { usePartnerCardIssueCriteriaStore } from "@/admin/store/partnerCardIssueCriteria";
import CardSettings from "@/shared/components/CardSettings.vue";
import AccountHolderCardSettings from "./AccountHolderCardSettings.vue";
import { useEmployerStore } from "@/admin/store/employer";
import { usePartnerStore } from "@/admin/store/partner";
import { PartnerSettingsPage } from "@/admin/route-names";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";
import { UserPermission } from "@/shared/types";
import _ from "lodash";

@Component({
  components: {
    CardSettings,
    AccountHolderCardSettings
  },
})
export default class EmployerCardSettings extends Vue {
  employerFeeScheduleStore = useEmployerFeeScheduleStore();
  employerInterestRateStore = useEmployerInterestRateStore();
  partnerCardIssueCriteriaStore = usePartnerCardIssueCriteriaStore();
  employerStore = useEmployerStore();
  partnerStore = usePartnerStore();
  cardIssueCriteria: EmployerCardIssueCriteriaResponse = null;
  showAccountHolderCardSettings = false;
  rolePermissionsStore = useRolePermissionsStore();

@Prop() employerId: number;

  get isLoaded () {
    return this.partnerCardIssueCriteriaStore.isCardIssueCriteriaLoadedForEmployer(this.employerId);
  }

  get employer (): Employer {
    const loadedEmployer = this.employerStore.employerDetail(this.employerId);
    return loadedEmployer.loaded && loadedEmployer.employer;
  }

  get issueCardCriteriaIdNum (): number {
    return this.cardIssueCriteria.issueCardCriteriaId;
  }

  get partnerIssueCardCriteriaIdNum () {
    if (this.partnerCardIssueCriteriaStore.cardIssueConfigs && this.partnerCardIssueCriteriaStore.cardIssueConfigs.partner) {
      return this.partnerCardIssueCriteriaStore.cardIssueConfigs.partner.issueCardCriteriaId;
    }

    return 0;
  }

  get isEmployerConfigActive () {
    return this.isEmployerConfigEnabled && this.cardIssueCriteria.dateEnabled !== null;
  }

  get isEmployerConfigEnabled () {
    return this.cardIssueCriteria !== null;
  }

  get isPartnerConfigEnabled () {
    return this.partnerCardIssueCriteriaStore.cardIssueConfigs !== null &&
      this.partnerCardIssueCriteriaStore.cardIssueConfigs.partner !== null;
  }

  get partnerId () {
    if (this.partnerCardIssueCriteriaStore.cardIssueConfigs) {
      return this.partnerCardIssueCriteriaStore.cardIssueConfigs.partnerId;
    }

    return 0;
  }

  get partnerName () {
    if (this.partnerId > 0) {
      return this.partnerStore.partnerName(this.partnerId);
    }

    return "";
  }

  get accountHolderCardSettingsLinkText () {
    return `${this.showAccountHolderCardSettings ? "Hide" : "View"} Card Issuance for Account Holders`;
  }

  get routeToPartnerCardSettings () {
    return { name: PartnerSettingsPage, params: { partnerId: this.partnerId, tab: "card-settings" } };
  }

  @Watch("isLoaded")
  copyCardIssueCriteria () {
    if (this.isLoaded) {
      this.cardIssueCriteria = _.cloneDeep(this.partnerCardIssueCriteriaStore.cardIssueCriteriaForEmployer(this.employerId));
    }
  }

  @Watch("employerId")
  onEmployerIdChanged () {
    this.partnerCardIssueCriteriaStore.setPartnerId(this.employer.partner.id);
    this.partnerCardIssueCriteriaStore.loadCardIssueConfigs(this.employer.partner.id);
    this.partnerCardIssueCriteriaStore.loadEmployerCardIssueCriteria(this.employerId);
    this.employerFeeScheduleStore.loadFeeSchedules(this.employerId);
    this.employerInterestRateStore.loadInterestRates(this.employerId);
  }

  mounted () {
    this.onEmployerIdChanged();
    this.copyCardIssueCriteria();
    this.partnerCardIssueCriteriaStore.loadCardIssueCustomizationOptions();
  }

  canActivate () {
    const isCardIssueCustomizationLoaded = this.partnerCardIssueCriteriaStore.isCardIssueCustomizationLoaded(this.issueCardCriteriaIdNum);

    if (isCardIssueCustomizationLoaded) {
      return this.partnerCardIssueCriteriaStore.canActivateCardIssuance(this.issueCardCriteriaIdNum);
    }
  }

  reload () {
    this.partnerCardIssueCriteriaStore.reloadEmployerCardIssueCriteria(this.employerId);
  }

  askDeletePrCardIssueCriteria (issueCardCriteriaId: number) {
    if (
      confirm(`Are you sure you would like to remove card issuance for the entire employer, ${this.employer.name}? Enabled individuals will still continue to receive cards.`)
    ) {
      this.deleteCardIssueCriteria(issueCardCriteriaId);
    }
  }

  deleteCardIssueCriteria (issueCardCriteriaId: number) {
    return this.partnerCardIssueCriteriaStore.deleteCardIssueCriteria(issueCardCriteriaId)
    .then(() => {
      this.reload();
    });
  }

  activateCardIssueCriteria (issueCardCriteriaId: number) {
    if (confirm("Are you sure you want to activate this criteria? Account holders will start getting issued cards immediately using the current criteria settings. If you are not sure, hit Cancel, and check the criteria settings before enabling.")) {
      return this.partnerCardIssueCriteriaStore.activateCardIssueCriteria(issueCardCriteriaId)
      .then(() => {
        this.reload();
      });
    }
  }

  addEmployerCardIssueCriteria () {
    const request: CreateCardIssueCriteriaRequest = {
      partnerId: this.employer.partner.id,
      employerId: this.employerId
    };

    return this.partnerCardIssueCriteriaStore.createCardIssueCriteria(request)
    .then(() => {
      this.reload();
    });
  }

  toggleAccountHolderCardSettingsVisibility () {
    this.showAccountHolderCardSettings = !this.showAccountHolderCardSettings;
  }

  get hasEditPermission (): boolean {
    return this.rolePermissionsStore.userHasAdminPermission(UserPermission.AdminPortalCardSettingsEdit);
  }

  get hasCardSettingAccess (): boolean {
    return this.rolePermissionsStore.userHasAdminPermission(UserPermission.AdminPortalCardSettingsEdit) ||
      this.rolePermissionsStore.userHasAdminPermission(UserPermission.AdminPortalCardSettingsView);
  }
}
